/**
 * 虛擬捲動視窗計算（縱向列 + 橫向時間）。
 */

import { xToTime, type TimeAxis } from './time-scale.js';

export interface RowWindow {
  /** 起始列索引（含）。 */
  readonly startIndex: number;
  /** 結束列索引（不含）。 */
  readonly endIndex: number;
}

/**
 * 依捲動位置算出應渲染的列區間。
 *
 * 尾端以「可視區底緣落在哪一列」直接推導（`ceil(bottom / rowHeight)`），
 * 不加無條件的 +1：視窗高度剛好是列高整數倍時不會多畫一列，
 * 視窗高度為 0（尚未量測到容器）時 overscan 以外不渲染任何列。
 */
export function visibleRowRange(
  scrollTop: number,
  viewportHeight: number,
  rowHeight: number,
  rowCount: number,
  overscan = 4,
): RowWindow {
  if (rowCount <= 0 || rowHeight <= 0) return { startIndex: 0, endIndex: 0 };
  const top = Math.max(0, scrollTop);
  const bottom = top + Math.max(0, viewportHeight);
  const first = Math.floor(top / rowHeight);
  const lastExclusive = Math.ceil(bottom / rowHeight);
  const startIndex = Math.max(0, first - overscan);
  const endIndex = Math.min(rowCount, Math.max(first, lastExclusive) + overscan);
  return { startIndex, endIndex: Math.max(startIndex, endIndex) };
}

export interface TimeWindow {
  readonly viewLeft: number;
  readonly viewRight: number;
  readonly fromMs: number;
  readonly toMs: number;
}

/** 依橫向捲動位置算出應渲染的時間區間（含 overscan 像素）。 */
export function visibleTimeRange(
  axis: TimeAxis,
  scrollLeft: number,
  viewportWidth: number,
  overscanPx = 240,
): TimeWindow {
  const viewLeft = Math.max(0, scrollLeft - overscanPx);
  const viewRight = Math.min(axis.totalWidth, scrollLeft + Math.max(0, viewportWidth) + overscanPx);
  return {
    viewLeft,
    viewRight,
    fromMs: xToTime(axis, viewLeft),
    toMs: xToTime(axis, Math.max(viewLeft, viewRight)),
  };
}
